import { AlertCircle, Calendar, CheckCircle, FileText, Loader2, MapPin, Upload } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useOverlay } from '../../contexts/OverlayContext';
import { typography } from '../../styles/typography';
import { getAllSites } from '../SiteManagement/siteApi';
import { IconButton, PrimaryButton } from '../ui';
import { fdocApi } from './FdocApi';

const getTodayDate = () => {
  const now = new Date();
  return now.toISOString().split('T')[0];
};

const resolveSiteValue = (site) => {
  return site?.site_id ?? site?.siteId ?? site?.id ?? site?._id ?? site?.site_code ?? site?.siteCode ?? '';
};

const FdocIn = ({ isOpen, onClose, onCollect, openOverlay, closeOverlay }) => {
  const [uploadType, setUploadType] = useState('');
  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(false);
  
  // Stage management: 'form' or 'upload'
  const [currentStage, setCurrentStage] = useState('form');
  
  // Upload state
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [error, setError] = useState('');
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  
  // Use openOverlay and closeOverlay from props if provided, otherwise try to get from context
  let overlayOpen, overlayClose;
  try {
    const overlayContext = useOverlay();
    overlayOpen = openOverlay || overlayContext?.openOverlay;
    overlayClose = closeOverlay || overlayContext?.closeOverlay;
  } catch (e) {
    // If useOverlay fails (not in context), use props only
    overlayOpen = openOverlay;
    overlayClose = closeOverlay;
  }

  // Fetch sites on component mount
  useEffect(() => {
    const fetchSites = async () => {
      setIsSitesLoading(true);
      try {
        const response = await getAllSites();
        const sitesList = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : [];
        
        const sorted = [...sitesList].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        
        setSites(sorted.length > 0 ? sorted : []);
      } catch (error) {
        console.error('Error fetching sites:', error);
        setSites([]);
      } finally {
        setIsSitesLoading(false);
      }
    };

    if (isOpen) {
      fetchSites();
    }
  }, [isOpen]);

  // Type options - OO means Outside Organisation
  const typeOptions = [
    { value: 'PO', label: 'PO (OO)' },
    { value: 'Invoice', label: 'Invoice (OO)' },
    { value: 'Estimation', label: 'Estimation/Quotation (OO)' },
    { value: 'Bill', label: 'Bill/Receipt (OO)' },
    { value: 'Amenity Booking Invoice', label: 'Amenity Booking Invoice' },
  ];

  const validateSelectedFilesForType = (type, files) => {
    const normalizedType = (type || '').toLowerCase();
    const rules = {
      po: { mustIncludeAny: ['po'], label: 'PO', errorHint: 'Filename should include "po" when Type is PO.' },
      invoice: {
        mustIncludeAny: ['invoice', 'inv'],
        label: 'Invoice',
        errorHint: 'Filename should include "invoice" (or "inv") when Type is Invoice.',
      },
      bill: {
        mustIncludeAny: ['bill', 'receipt'],
        label: 'Bill',
        errorHint: 'Filename should include "bill" (or "receipt") when Type is Bill.',
      },
      estimation: {
        mustIncludeAny: ['estimation', 'estimate', 'quotation', 'quote'],
        label: 'Estimation/Quotation',
        errorHint: 'Filename should include "estimation" or "quotation" (or "quote") when Type is Estimation/Quotation.',
      },
    };

    const rule = rules[normalizedType];
    if (!rule) return { ok: true };

    const invalid = (files || []).filter((f) => {
      const name = (f?.name || '').toLowerCase();
      return !rule.mustIncludeAny.some((needle) => name.includes(needle));
    });
    if (invalid.length === 0) return { ok: true };

    return {
      ok: false,
      errorHint: rule.errorHint,
      message:
        invalid.length === 1
          ? `For ${rule.label} uploads, the filename should include ${rule.mustIncludeAny
              .map((v) => `"${v}"`)
              .join(' or ')}.\n\nInvalid file: ${invalid[0].name}`
          : `For ${rule.label} uploads, the filename should include ${rule.mustIncludeAny
              .map((v) => `"${v}"`)
              .join(' or ')}.\n\nInvalid files:\n- ${invalid
              .map((f) => f.name)
              .join('\n- ')}`,
    };
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validation = validateSelectedFilesForType(uploadType, files);
    if (!validation.ok) {
      setSelectedFiles([]);
      setError(validation.message || validation.errorHint || 'Invalid filename for selected Type.');
      // Reset input so selecting the same file again triggers onChange
      e.target.value = '';
      return;
    }

    setSelectedFiles(files);
    setError('');
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    // Reset form
    setUploadType('');
    setSelectedDate(getTodayDate());
    setSelectedFiles([]);
    setSelectedSite(null);
    setError('');
    setUploadProgress([]);
    setUploadedDocuments([]);
    setIsUploading(false);
    setCurrentStage('form'); // Reset to form stage
    onClose();
  };

  // Helper function to get file content type
  const getContentType = (file) => {
    if (file.type) {
      return file.type;
    }
    // Fallback based on file extension
    const extension = file.name.split('.').pop().toLowerCase();
    const contentTypeMap = {
      pdf: 'application/pdf',
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      xls: 'application/vnd.ms-excel',
      csv: 'text/csv',
    };
    return contentTypeMap[extension] || 'application/octet-stream';
  };

  // Upload a single file to S3 using presigned URL
  const uploadFileToS3 = async (file, presignedData) => {
    const formData = new FormData();
    
    // Add all the fields from presigned_post.fields
    Object.entries(presignedData.presigned_post.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    
    // Add the file last
    formData.append('file', file);

    // Upload to S3
    const response = await fetch(presignedData.presigned_post.url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`S3 upload failed: ${response.status} - ${errorText}`);
    }

    return response;
  };

  // Handle upload button - Switch to upload stage
  const handleUpload = () => {
    if (!uploadType || !selectedDate || !selectedSite || selectedFiles.length === 0) {
      setError('Please fill in all required fields: Type, Site, File Type, and Select Date');
      return;
    }

    const validation = validateSelectedFilesForType(uploadType, selectedFiles);
    if (!validation.ok) {
      setError(validation.message || validation.errorHint || 'Invalid filename for selected Type.');
      return;
    }

    // Switch to upload stage
    setCurrentStage('upload');
    setError('');
    // Start upload process
    handleCollect();
  };

  // Handle collect button - Upload files with presigned URLs
  const handleCollect = async () => {
    if (!uploadType || !selectedDate || !selectedSite || selectedFiles.length === 0) {
      return;
    }

    const validation = validateSelectedFilesForType(uploadType, selectedFiles);
    if (!validation.ok) {
      setError(validation.message || validation.errorHint || 'Invalid filename for selected Type.');
      setCurrentStage('form');
      return;
    }

    setIsUploading(true);
    setError('');
    setUploadProgress([]);
    setUploadedDocuments([]);

    const uploadResults = [];
    const progressArray = selectedFiles.map(() => ({ status: 'pending', message: '' }));
    setUploadProgress([...progressArray]);

    try {
      // Process each file
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        
        try {
          // Update progress: Getting presigned URL
          progressArray[i] = { status: 'presigning', message: 'Getting upload URL...' };
          setUploadProgress([...progressArray]);

          // Step 1: Get presigned URL
          const contentType = getContentType(file);
          const siteId = resolveSiteValue(selectedSite);
          const presignedData = await fdocApi.presignUpload({
            file_name: file.name,
            content_type: contentType,
            site_id: siteId,
            document_type: uploadType || undefined, // So backend can persist type (e.g. Amenity Booking Invoice)
          });

          console.log('Presigned data received:', presignedData);

          // Update progress: Uploading to S3
          progressArray[i] = { status: 'uploading', message: 'Uploading to S3...' };
          setUploadProgress([...progressArray]);

          // Step 2: Upload file to S3
          await uploadFileToS3(file, presignedData);

          console.log('File uploaded to S3:', file.name);

          // Update progress: Success
          progressArray[i] = { status: 'success', message: 'Upload complete!' };
          setUploadProgress([...progressArray]);

          // Store upload result
          uploadResults.push({
            fileName: file.name,
            fdoc_id: presignedData.fdoc_id,
            object_key: presignedData.object_key,
            bucket: presignedData.bucket,
            success: true,
          });
        } catch (fileError) {
          console.error(`Error uploading file ${file.name}:`, fileError);
          progressArray[i] = { 
            status: 'error', 
            message: fileError.message || 'Upload failed' 
          };
          setUploadProgress([...progressArray]);

          uploadResults.push({
            fileName: file.name,
            success: false,
            error: fileError.message,
          });
        }
      }

      // Store uploaded documents
      setUploadedDocuments(uploadResults);

      // Check if all uploads were successful
      const allSuccessful = uploadResults.every(result => result.success);
      
      if (allSuccessful) {
        // Call the onCollect callback with the form data and upload results
        if (onCollect) {
          onCollect({
            type: uploadType,
            date: selectedDate,
            files: selectedFiles,
            uploadedDocuments: uploadResults,
          });
        }

        // Close FdocIn overlay
        if (overlayClose) {
          overlayClose('fdocIn');
        } else {
          onClose();
        }
        
        // Open FdocInPreview for the first uploaded document (or handle multiple documents)
        if (uploadResults.length > 0 && uploadResults[0].success && overlayOpen) {
          // Get full document details to pass to preview
          try {
            const documentData = await fdocApi.getDocument(uploadResults[0].fdoc_id);
            // Open Document Details (Extraction) overlay
            overlayOpen('fdocInPreview', {
              documentData: {
                fdoc_id: uploadResults[0].fdoc_id,
                // Provide upload context so preview can auto-fill basic info even if extraction is pending/empty
                document_type: uploadType || undefined,
                document_date: selectedDate || undefined,
                ...documentData,
              }
            });
          } catch (err) {
            console.error('Error fetching document for preview:', err);
            // Still open preview with basic data
            overlayOpen('fdocInPreview', {
              documentData: {
                fdoc_id: uploadResults[0].fdoc_id,
                file_name: uploadResults[0].fileName,
                document_type: uploadType || undefined,
                document_date: selectedDate || undefined,
              }
            });
          }
        }
      } else {
        // Some uploads failed
        const failedCount = uploadResults.filter(r => !r.success).length;
        setError(`${failedCount} file(s) failed to upload. Please try again.`);
      }
    } catch (err) {
      console.error('Error in upload process:', err);
      setError(err.message || 'Failed to upload files. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Dialog Box */}
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="w-[464px] h-auto max-h-[90vh] relative bg-neutral-50/50 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-neutral-300/20 backdrop-blur-[30px] overflow-hidden">
          {/* Header */}
          <div className="w-full px-8 pt-6 pb-4 flex justify-between items-center">
            <div className="flex flex-col">
              <h3 className={typography.h3} style={{ color: '#000000' }}>
                Upload Fdoc
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Upload your Fdoc document
              </p>
            </div>
            {/* Close Button */}
            <IconButton
              onClick={handleCloseDialog}
              icon={
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            />
          </div>

          {/* Dialog Content */}
          <div className="px-8 pb-6 space-y-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {currentStage === 'form' ? (
              <>
                {/* Form Stage: Type, Site, File Type, Date */}
                {/* Type Dropdown */}
                <div>
                  <label
                    htmlFor="upload-type"
                    className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
                  >
                    <FileText className="w-4 h-4" />
                    Type
                  </label>
                  <select
                    id="upload-type"
                    value={uploadType}
                    onChange={(e) => setUploadType(e.target.value)}
                    className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  >
                    <option value="">Select type</option>
                    {typeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {/* OO Explanation Note */}
                  <p className="mt-2 text-xs text-gray-500 italic">
                    * OO stands for Outside Organisation
                  </p>
                </div>

                {/* Site Selection Dropdown */}
                <div>
                  <label
                    htmlFor="upload-site"
                    className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
                  >
                    <MapPin className="w-4 h-4" />
                    Select Site
                  </label>
                  <select
                    id="upload-site"
                    value={selectedSite ? resolveSiteValue(selectedSite) : ''}
                    onChange={(e) => {
                      const siteId = e.target.value;
                      const site = sites.find(s => resolveSiteValue(s) === siteId);
                      setSelectedSite(site || null);
                    }}
                    className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                    required
                    disabled={isSitesLoading}
                  >
                    <option value="">
                      {isSitesLoading ? 'Loading sites...' : 'Select a site'}
                    </option>
                    {sites.map((site) => (
                      <option key={resolveSiteValue(site)} value={resolveSiteValue(site)}>
                        {site.site_name || site.name || 'Unknown Site'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* File Upload */}
                <div>
                  <label
                    htmlFor="file-upload"
                    className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
                  >
                    <Upload className="w-4 h-4" />
                    File Type
                  </label>
                  <div className="relative">
                    <input
                      id="file-upload"
                      type="file"
                      multiple
                      onChange={handleFileChange}
                      className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                    />
                  </div>
                  {selectedFiles.length > 0 && (
                    <div className="mt-2 text-sm text-gray-600">
                      {selectedFiles.length} file(s) selected
                    </div>
                  )}
                </div>

                {/* Date Picker */}
                <div>
                  <label
                    htmlFor="upload-date"
                    className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
                  >
                    <Calendar className="w-4 h-4" />
                    Select Date
                  </label>
                  <input
                    id="upload-date"
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-red-800">Error</p>
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                )}

                {/* Upload Documents Button */}
                <div className="pt-4">
                  <PrimaryButton
                    onClick={handleUpload}
                    disabled={
                      !uploadType ||
                      !selectedSite ||
                      !selectedDate ||
                      selectedFiles.length === 0
                    }
                    className="w-full"
                  >
                    Upload Documents
                  </PrimaryButton>
                </div>
              </>
            ) : (
              <>
                {/* Upload Progress Stage */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Uploading Documents...
                  </h4>
                  
                  {/* Upload Progress */}
                  {uploadProgress.length > 0 && (
                    <div className="space-y-2">
                      {uploadProgress.map((progress, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg"
                        >
                          {progress.status === 'pending' && (
                            <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                          )}
                          {progress.status === 'presigning' && (
                            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                          )}
                          {progress.status === 'uploading' && (
                            <Loader2 className="w-5 h-5 text-green-600 animate-spin" />
                          )}
                          {progress.status === 'success' && (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          )}
                          {progress.status === 'error' && (
                            <AlertCircle className="w-5 h-5 text-red-600" />
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-800">
                              {selectedFiles[index]?.name}
                            </p>
                            {progress.message && (
                              <p
                                className={`text-xs ${
                                  progress.status === 'error'
                                    ? 'text-red-600'
                                    : progress.status === 'success'
                                    ? 'text-green-600'
                                    : 'text-gray-600'
                                }`}
                              >
                                {progress.message}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Error Message */}
                  {error && (
                    <div className="mt-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-red-800">Error</p>
                        <p className="text-sm text-red-700">{error}</p>
                      </div>
                    </div>
                  )}

                  {/* Back Button (only show if not uploading and there's an error) */}
                  {!isUploading && error && (
                    <div className="pt-4">
                      <PrimaryButton
                        onClick={() => {
                          setCurrentStage('form');
                          setError('');
                        }}
                        className="w-full"
                      >
                        Back to Form
                      </PrimaryButton>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default FdocIn;

